import KeyIcon from '@mui/icons-material/Key';
import { MenuItem } from '@mui/material';
import { HTTP_STATUS_CODE } from 'api/api';
import { useDeleteSSOConfigs, useSSOStatus } from 'api/configurations';
import ConfigurationStatusCard from 'components/home/ConfigurationsStatus/ConfigurationStatusCards/ConfigurationStatusCard';
import ThreeDotMenu from 'components/shared/ThreeDotMenu';
import { useDialogsContext } from 'contexts/Dialogs/DialogsContext';
import { useFeatureFlags } from 'contexts/FeatureFlagsContext';
import { useIdPConfigContext } from 'contexts/IdPConfigContext';
import React, { useCallback } from 'react';
import { FeatureFlagId } from 'types/featureflags';
import { useSSOConfigCardNavigate } from 'utils/navigation';
import AddCta from './AddCta';
import ConfiguredMark from './ConfiguredMark';

const SSOConfigStatusCard: React.FC = () => {
	const { setMethod, setIdPID, isSubTenant } = useIdPConfigContext();

	const {
		data: ssoStatus,
		isLoading: isSSOStatusLoading,
		isIdle: isSSOStatusIdle
	} = useSSOStatus();

	const ssoConfigCardNavigate = useSSOConfigCardNavigate();

	const deleteMutation = useDeleteSSOConfigs();

	const { openDeleteSSOConfigDialog } = useDialogsContext();

	const { isFeatureFlagActive } = useFeatureFlags();
	const hideSSODelete = isFeatureFlagActive(FeatureFlagId.HIDE_SSO_DELETE);

	const loader = isSSOStatusLoading || isSSOStatusIdle;

	const handleDelete = useCallback(async () => {
		const { proceed } = await openDeleteSSOConfigDialog();
		if (!proceed) {
			return;
		}
		try {
			const { status } = await deleteMutation.mutateAsync();
			// make sure to reset the current idp and method if the deletion was successful
			if (status === HTTP_STATUS_CODE.ok) {
				setIdPID(null);
				setMethod(null);
			}
		} catch (err) {
			/* empty */
		}
	}, [openDeleteSSOConfigDialog, deleteMutation, setIdPID, setMethod]);

	const handleClick = useCallback(() => {
		/* istanbul ignore if: no need to test this */
		if (loader) {
			return;
		}
		ssoConfigCardNavigate();
	}, [loader, ssoConfigCardNavigate]);

	if (loader) {
		return (
			<ConfigurationStatusCard
				disabled
				icon={<KeyIcon fontSize="large" />}
				title={intl.tr('general.SSOConfiguration')}
				testIdTopic="sso"
			/>
		);
	}

	let title = intl.tr('general.SSOConfiguration');

	if (ssoStatus?.method) {
		title += ` (${intl.tr('general.methodUpper', { method: ssoStatus.method })})`;
	}

	return (
		<ConfigurationStatusCard
			disabled={loader}
			onClick={handleClick}
			icon={<KeyIcon fontSize="large" />}
			title={title}
			idp={ssoStatus?.idp}
			testIdTopic="sso"
			footerContent={
				<>
					{ssoStatus?.ssoConfigured && (
						<>
							<ConfiguredMark />
							{!isSubTenant && (
								<ThreeDotMenu>
									<MenuItem onClick={handleClick} data-testid="edit-sso-config">
										{intl.tr('general.edit')}
									</MenuItem>
									{!hideSSODelete && (
										<MenuItem
											onClick={handleDelete}
											data-testid="delete-sso-config"
										>
											{intl.tr('general.delete')}
										</MenuItem>
									)}
								</ThreeDotMenu>
							)}
						</>
					)}
					{!ssoStatus?.idp && <AddCta />}
				</>
			}
		/>
	);
};

export default SSOConfigStatusCard;
